import Image from "next/image";
import iconInstagram from "../public/img/icons/instagram.png";
import iconUser from "../public/img/icons/user.png";
import {Fragment, useEffect, useRef, useState} from "react";
import {getConceptFriendlyName, getTrainerVideoUrl} from "../helpers/trainers";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";

export default function TrainerCard({trainer, active, onMouseEnter, onMouseOut, className = '', ...rest}) {
    const [hiddenThumb, setHiddenThumb] = useState(false);
    const refVideo = useRef();
    const [hasLoaded, setHasLoaded] = useState(false);
    const refContainer = useRef(null);

    const isVisible = useIntersectionObserver(refContainer, {
        threshold: 0.1,
        rootMargin: '200px',
    });

    useEffect(() => {
        if (!hasLoaded && isVisible && refVideo.current) {
            const source = refVideo.current.querySelector('source');
            if (source && source.dataset.src) {
                source.src = source.dataset.src;
                refVideo.current.load();
                setHasLoaded(true);
            }
        }
    }, [isVisible, hasLoaded]);

    useEffect(() => {
        if (refVideo.current) {
            active ? refVideo.current.play() : refVideo.current.pause();
        }

        setHiddenThumb(active);
    }, [active]);

    const handleMouseEnter = () => {
        onMouseEnter();
    }

    const handleMouseOut = () => {
        onMouseOut();
    }

    return (
        <article
            ref={refContainer}
            className={`relative w-full flex bg-black ${active ? 'aspect-[6/7]' : 'aspect-[1/1] md:aspect-[6/7]'} rounded-lg overflow-hidden flex flex-col justify-end transition-all duration-300 ${className}`}
            onMouseOver={handleMouseEnter}
            onMouseOut={handleMouseOut}
            onClick={handleMouseEnter}
            {...rest}
        >
            <div className="overlay pointer-events-none flex">
                <video
                    className={`overlay object-cover rounded-lg transition-opacity duration-300 ${hiddenThumb ? 'opacity-100' : 'opacity-0'}`}
                    loop={true}
                    playsInline={true}
                    muted={true}
                    ref={refVideo}
                >
                    <source data-src={getTrainerVideoUrl(trainer.cleanName)} type="video/mp4"/>
                </video>

                <div
                    className={`relative flex w-full transition-opacity duration-300 ${hiddenThumb ? 'opacity-0' : 'opacity-100'}`}>
                    <Image src={trainer.image}
                           alt={trainer.name}
                           className="rounded-lg w-full h-full object-cover"
                           style={{
                               objectPosition: 'center 20%'
                           }}
                           sizes="(min-width: 1024px) 380px, (min-width: 768px) 50vw, 100vw"
                    />
                </div>
            </div>

            <div
                className={`absolute bottom-0 left-0 w-full ${active ? 'h-1/2 from-black' : 'h-1/3 md:h-1/2 from-black/80 md:from-black'} bg-gradient-to-t to-black/0 pointer-events-none transition-all duration-200`}
            />

            <div className="relative pb-5 md:pb-9 px-5 md:px-9 text-white">
                <header>
                    <h2 className="text-2xl font-semibold">{trainer.name}</h2>
                </header>
                <div
                    className={`accordion accordion-0 accordion-solid ${active ? 'accordion-open' : ''}`}>
                    <div
                        className={`pt-5 flex flex-col gap-4 transition-opacity duration-300 ${active ? 'opacity-100' : 'opacity-0'}`}>

                        {trainer.instagram && trainer.instagram !== '' && (
                            <div className="flex items-start gap-4">
                                <Image src={iconInstagram}
                                       alt="Instagram icon"
                                       className="w-5"
                                />
                                <div className="relative flex-1 leading-[1.2] font-semibold">
                                    <a href={`https://instagram.com/${trainer.instagram}`} target="_blank"
                                       rel="nofollow noreferrer"
                                       className="inline-flex mouse-hover:underline"
                                    >
                                        @{trainer.instagram}
                                    </a>
                                </div>
                            </div>
                        )}

                        <div className="flex items-start gap-4">
                            <Image src={iconUser}
                                   alt="User icon"
                                   className="w-5"
                            />
                            <div className="relative flex-1 leading-[1.2] font-semibold">
                                {trainer.concepts.map((concept, index) => {
                                    return (
                                        <Fragment key={`Concept: ${trainer.name} - ${concept}`}>
                                            {getConceptFriendlyName(concept)}{index < trainer.concepts.length - 1 ? ', ' : ''}
                                        </Fragment>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    )
}